/**
 * useStableChartData — keep the array handed to a Recharts chart referentially
 * stable, because an unstable one kills the page.
 *
 * ── The failure ─────────────────────────────────────────────────────────────
 * Recharts v3 keeps chart data in an internal Redux store. Its
 * `ChartDataContextProvider` runs an effect keyed on the `data` array IDENTITY:
 * whenever that identity changes it dispatches `setChartData(undefined)` in the
 * cleanup and `setChartData(next)` on the way back in. Every dispatch notifies
 * the store, which re-renders the chart subtree — and if that render produces
 * ANOTHER new array, the effect fires again. React stops the cycle with
 *
 *     Maximum update depth exceeded
 *
 * and the page's error boundary takes over. The stack surfaces inside the
 * passive-UNMOUNT cascade, so it reads as a chart-teardown crash rather than
 * what it is: a new array on every render.
 *
 * Five chart components were doing exactly that, across BOTH web portals: two
 * spread inline in JSX (`data={[...data]}`), throwing away memoisation their
 * callers had correctly done, and three derived their rows inside the component
 * with a bare `.map()` / `.sort()`. It lives here, not in an app, because the
 * trap is a property of Recharts and both portals draw charts.
 *
 * ── Why keyed by CONTENT, not by reference ──────────────────────────────────
 * A plain `useMemo(..., [data])` only helps when the CALLER memoised too, and a
 * caller that forgets doesn't get a warning — it gets a dead page. Chart
 * datasets here are a handful of tiny rows, so identifying them by a cheap
 * content key makes the component impossible to melt from the outside, which is
 * the right trade for a crash of this severity.
 */

import { useRef } from 'react';

/**
 * @param data rows for `<RadarChart data={…}>` — may be a fresh array each render
 * @param key  cheap identity of those rows (e.g. `"Hierarchy:42|Directness:18"`);
 *             the SAME key must mean the same chart content
 * @returns the first array seen for `key`, kept until the key changes
 */
export function useStableChartData<T>(data: T[], key: string): T[] {
  const held = useRef<{ key: string; data: T[] }>({ key, data });
  if (held.current.key !== key) {
    held.current = { key, data };
  }
  return held.current.data;
}
